import React, { useEffect, useRef, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ScanFace, Camera, LogIn, LogOut, Loader2, CheckCircle2, AlertTriangle,
  RefreshCw, MapPin, ShieldCheck, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FacePunch = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // 'in' | 'out' | 'enroll'
  const [enrolled, setEnrolled] = useState(null); // null=loading, true/false
  const [lastPunch, setLastPunch] = useState(null);
  const [coords, setCoords] = useState(null);

  const stopCamera = useCallback(() => {
    const s = videoRef.current?.srcObject;
    if (s) {
      s.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 480, height: 360, facingMode: 'user' }, audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
      }
    } catch (e) {
      setError(e.message || 'Camera access denied. Please allow webcam permission in your browser.');
    }
  }, []);

  // Request geolocation (best-effort; not blocking).
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {}, { timeout: 5000 },
    );
  }, []);

  // Fetch enrollment status on mount.
  useEffect(() => {
    axios.get(`${API_URL}/attendance/face-enrollment/me`, { withCredentials: true })
      .then(({ data }) => setEnrolled(!!data.enrolled))
      .catch(() => setEnrolled(false));
  }, []);

  // Start the camera automatically on mount; stop on unmount.
  useEffect(() => { startCamera(); return stopCamera; }, [startCamera, stopCamera]);

  const captureBase64 = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.72);
  };

  const doPunch = async (punch_type) => {
    const img = captureBase64();
    if (!img) { toast.error('Camera not ready — allow permission and retry'); return; }
    setBusy(punch_type);
    try {
      const { data } = await axios.post(`${API_URL}/attendance/face-punch`, {
        image_base64: img,
        punch_type,
        latitude: coords?.lat,
        longitude: coords?.lon,
      }, { withCredentials: true });
      toast.success(`Punched ${punch_type.toUpperCase()} at ${new Date(data.punch_time).toLocaleTimeString()}`);
      setLastPunch(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Punch failed');
    }
    setBusy(null);
  };

  const doEnroll = async () => {
    const img = captureBase64();
    if (!img) { toast.error('Camera not ready'); return; }
    setBusy('enroll');
    try {
      await axios.post(`${API_URL}/attendance/face-enroll`, { image_base64: img }, { withCredentials: true });
      toast.success('Reference face saved');
      setEnrolled(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enrollment failed');
    }
    setBusy(null);
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="face-punch-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <ScanFace className="w-8 h-8 text-[#0052FF]" /> Face Attendance
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Mark your attendance using your webcam. The captured image is stored for audit and logged under face-recognition punches.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Camera preview + punch */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5" data-testid="face-punch-camera-card">
            <div className="relative rounded-lg overflow-hidden bg-slate-900 aspect-video flex items-center justify-center">
              <video ref={videoRef} playsInline muted data-testid="face-punch-video"
                     className={`w-full h-full object-cover ${streaming ? '' : 'opacity-40'}`} />
              <canvas ref={canvasRef} className="hidden" />
              {!streaming && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 p-6 text-center">
                  <Camera className="w-10 h-10 opacity-80" />
                  {error
                    ? <p className="text-sm text-red-200 max-w-sm">{error}</p>
                    : <p className="text-sm opacity-80">Starting camera…</p>}
                  <Button onClick={startCamera} variant="outline" data-testid="face-punch-retry-camera"
                          className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
                  </Button>
                </div>
              )}
              {streaming && (
                <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-black/60 text-white text-[11px] font-medium flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button
                data-testid="face-punch-in-btn"
                onClick={() => doPunch('in')}
                disabled={!streaming || busy === 'in'}
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {busy === 'in' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                Punch IN
              </Button>
              <Button
                data-testid="face-punch-out-btn"
                onClick={() => doPunch('out')}
                disabled={!streaming || busy === 'out'}
                className="h-12 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {busy === 'out' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogOut className="w-4 h-4 mr-2" />}
                Punch OUT
              </Button>
            </div>
          </div>

          {/* Side panel: enrollment + status + last punch */}
          <div className="space-y-4" data-testid="face-punch-side-panel">
            <div className={`rounded-xl p-4 border ${enrolled ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-3">
                {enrolled ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                          : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${enrolled ? 'text-emerald-900' : 'text-amber-900'}`} data-testid="face-punch-enroll-status">
                    {enrolled === null ? 'Checking enrollment…' : enrolled ? 'Face enrolled' : 'Face not enrolled'}
                  </p>
                  <p className={`text-xs mt-0.5 ${enrolled ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {enrolled
                      ? 'Your reference photo is on file. Punches will be matched against it.'
                      : 'Capture a clear front-facing photo so future punches can be verified against a reference.'}
                  </p>
                </div>
              </div>
              <Button
                onClick={doEnroll}
                disabled={!streaming || busy === 'enroll'}
                data-testid="face-punch-enroll-btn"
                variant="outline"
                className="w-full mt-3 bg-white"
              >
                {busy === 'enroll' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />}
                {enrolled ? 'Re-enroll Face' : 'Enroll Face'}
              </Button>
            </div>

            {lastPunch && (
              <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="face-punch-last-result">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Last punch</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {lastPunch.punch_type?.toUpperCase()} · {new Date(lastPunch.punch_time).toLocaleString()}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{lastPunch.employee_code}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-1.5">
              <p className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Info className="w-3.5 h-3.5" /> How it works
              </p>
              <p>· Your webcam captures a still frame on each punch.</p>
              <p>· The image is logged with verification type <span className="font-mono">face</span>.</p>
              <p className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3" />
                Location: {coords ? `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}` : 'not captured'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FacePunch;
